import { useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import debounce from 'lodash.debounce';
import { AppState } from '../../../../../store/store';
import { updateAiAssistantContent } from '../../../../../store/action-creators/ai-tools';
import { isArticleEdit } from '../ai-assistant.helper';

export function useAiAssistantSuggestions(field: 'main_title' | 'summary' | 'seo_title') {
	const dispatch = useDispatch();
	const state = useSelector((state: AppState) => state);

	const article =
		isArticleEdit() && state.tempArticle && state.tempArticle.articleEdit ? state.tempArticle.articleEdit : state.tempArticle.article;

	const aiAssistantContent = state.aiTools.aiAssistantContent;

	if (!aiAssistantContent || !aiAssistantContent[field]) {
		throw new Error(`Invalid field '${field}' used in useAiAssistantSuggestions`);
	}

	const extracted = Array.isArray(aiAssistantContent[field].extracted_content) ? aiAssistantContent[field].extracted_content : [];

	const [suggestions, setSuggestions] = useState<string[]>([...extracted]);

	// Track which suggestion is selected
	const initialSelectedIndex = useMemo(() => {
		const value = aiAssistantContent[field].current_content;
		if (value === null || typeof value !== 'string') return -1;
		return extracted.findIndex((s) => s === value);
	}, [aiAssistantContent, extracted, field]);

	const [selectedIndex, setSelectedIndex] = useState<number>(initialSelectedIndex);

	// Track which indices have been edited
	const [editedIndices, setEditedIndices] = useState<Set<number>>(new Set());

	const articleValue = useMemo(() => {
		if (field === 'main_title') return article && article.title ? article.title : '';
		if (field === 'summary') return article && article.subtitle ? article.subtitle : '';
		if (field === 'seo_title') return article && article.seo && article.seo.title ? article.seo.title : '';
		return '';
	}, [field, article]);

	const currentSelectedValue = selectedIndex >= 0 ? suggestions[selectedIndex] : null;

	const debouncedDispatch = useCallback(
		debounce((updatedSuggestions: string[], selected: string | null) => {
			dispatch(
				updateAiAssistantContent({
					[field]: {
						current_content: selected,
						extracted_content: updatedSuggestions,
					},
				}),
			);
		}, 300),
		[dispatch, field],
	);

	// Select or deselect suggestion
	const handleSelect = (value: string, index: number) => {
		if (selectedIndex === index) {
			setSelectedIndex(-1);
			debouncedDispatch(suggestions, null);
		} else {
			setSelectedIndex(index);
			debouncedDispatch(suggestions, value);
		}
	};

	// Edit suggestion text
	const handleEdit = (value: string, index: number) => {
		const updated = [...suggestions];
		updated[index] = value;
		setSuggestions(updated);

		const newEdited = new Set(editedIndices);
		newEdited.add(index);
		setEditedIndices(newEdited);

		// Preserve selection if editing selected one
		const updatedSelected = index === selectedIndex ? value : currentSelectedValue;
		debouncedDispatch(updated, updatedSelected);
	};

	const valueLengths = useMemo(() => {
		return suggestions.map((v) => v.length);
	}, [suggestions]);

	return {
		suggestions,
		articleValue,
		currentSelectedValue,
		valueLengths,
		handleSelect,
		handleEdit,
		selectedIndex,
		editedIndices,
	};
}
